// Style
import "./split-pane.scss";
// Tools
import React, {
	useRef,
	useState,
	useImperativeHandle,
	useLayoutEffect,
} from "react";
// Utils
import { dragToReorder } from "./drag/dragToReorder";
import calculateCurrrentWeight from "./weights/calculate-current-weight";
import calculateCurrentSections from "./weights/calculate-current-sections";
import updateModuleOrder from "./weights/updateModuleOrder";
import { useSplitPaneLayoutInteractive } from "./use-split-pane-layout-interactive";

type Direction = "vertical" | "horizontal";

type LegacyProps = { top?: React.ReactNode; bottom?: React.ReactNode };

type SplitPaneProps = {
	panes?: React.ReactNode[]; // поддерживает 1..3
	direction?: Direction;
	minPercent?: number;
	// Опционально: стартовые проценты (для 2 => [p], для 3 => [p1,p2])
	initialPercents?: number[];
} & LegacyProps;

export type SplitPaneHandle = {
	// Сбрасывает деление на равные части
	reset: () => void;
	// Установить проценты (для 2: [p], для 3: [p1,p2])
	setPercents?: (p: number[]) => void;
};

// Конвертируем сжатое представление [p1, p2?] в абсолютное [p1, p2, p3]
const toAbsolutePercents = (percents: number[], count: number): number[] => {
	if (count === 1) return [100];
	if (count === 2) return [percents[0], 100 - percents[0]];
	if (count === 3) {
		const p1 = percents[0] ?? 0;
		const p2 = percents[1] ?? 0;
		return [p1, p2, 100 - p1 - p2];
	}
	return [];
};

// Конвертируем абсолютное [p1, p2, p3] обратно в сжатое [p1, p2?]
const fromAbsolutePercents = (abs: number[], count: number): number[] => {
	if (count <= 1) return [];
	if (count === 2) return [abs[0]];
	return [abs[0], abs[1]];
};

export const SplitPane = React.forwardRef<SplitPaneHandle, SplitPaneProps>(
	(props, ref) => {
		const {
			panes: panesProp,
			top,
			bottom,
			direction = "vertical",
			minPercent = 6,
		} = props;

		// Для обратной совместимости: если переданы top/bottom, используем их
		const panes: React.ReactNode[] =
			panesProp && panesProp.length > 0
				? panesProp
				: top || bottom
					? ([top, bottom].filter(Boolean) as React.ReactNode[])
					: [];

		const paneIdentitySig = panes
			.map((p, i) => String((p as React.ReactElement)?.key ?? i))
			.join(",");

		const containerRef = useRef<HTMLDivElement | null>(null);
		const isHorizontal = direction === "horizontal";
		const isLayoutInteractive = useSplitPaneLayoutInteractive();

		// Внутреннее состояние порядка панелей — позволяет менять порядок без перерендера родителя
		const [internalPanes, setInternalPanes] =
			useState<React.ReactNode[]>(panes);

		// Синхронизируем internalPanes с пропами при смене набора панелей (по ключам), не только при смене длины —
		// иначе «только dashboard» ↔ «только chat» обе дают length===1 и остаётся устаревший узел.
		React.useEffect(() => {
			setInternalPanes(panes);
			// eslint-disable-next-line react-hooks/exhaustive-deps -- paneIdentitySig стабильнее panes (новый массив каждый рендер)
		}, [paneIdentitySig]);

		// Состояние процентов: для 2 панелей -> [p], для 3 панелей -> [p1,p2]
		const defaultPercents =
			panes.length === 3 ? [33.333, 33.333] : panes.length === 2 ? [50] : [];
		const initial = () => {
			if (
				props.initialPercents &&
				props.initialPercents.length === panes.length
			) {
				// Ограничиваем входящие значения
				return props.initialPercents.map((v) => {
					const min = minPercent;
					const max = 100 - minPercent;
					return Math.max(min, Math.min(max, v));
				});
			}
			return defaultPercents;
		};

		const [percents, setPercents] = useState<number[]>(initial);
		const draggingIndex = useRef<number | null>(null);

		const clamp = (v: number, a: number, b: number) =>
			Math.max(a, Math.min(b, v));

		// Хранит визуальные позиции панелей ДО перестановки — для FLIP-анимации
		const flipOldPositions = useRef<Map<HTMLElement, number> | null>(null);

		// Ref для актуального колбэка reorder (чтобы dragToReorder всегда видел свежее состояние)
		const handleReorderRef = useRef<
			(from: number, to: number, oldPositions: Map<HTMLElement, number>) => void
		>(() => {});

		const handleReorder = (
			from: number,
			to: number,
			oldPositions: Map<HTMLElement, number>,
		) => {
			flipOldPositions.current = oldPositions;
			setInternalPanes((prev) => {
				const next = [...prev];
				const [moved] = next.splice(from, 1);
				next.splice(to, 0, moved);

				// Обновляем moduleOrder в localStorage
				const newKeys = next.map((p) =>
					String((p as React.ReactElement)?.key ?? "").trim(),
				);
				updateModuleOrder(newKeys);

				return next;
			});

			setPercents((prev) => {
				const abs = toAbsolutePercents(prev, internalPanes.length);
				// Меняем позиции в абсолютных процентах
				const nextAbs = [...abs];
				const [movedVal] = nextAbs.splice(from, 1);
				nextAbs.splice(to, 0, movedVal);
				// Сохраняем веса через существующую утилиту (вызов отложим через setTimeout, чтобы стейт успел обновиться)
				setTimeout(() => {
					const newPanes = (() => {
						const p = [...internalPanes];
						const [m] = p.splice(from, 1);
						p.splice(to, 0, m);
						return p;
					})();
					if (isHorizontal) {
						calculateCurrrentWeight(newPanes, nextAbs);
					} else {
						calculateCurrentSections(newPanes, nextAbs);
					}
				}, 0);
				return fromAbsolutePercents(nextAbs, internalPanes.length);
			});
		};

		// Обновляем ref при каждом рендере, чтобы dragToReorder не держал устаревший колбэк
		handleReorderRef.current = handleReorder;

		// FLIP-анимация после перестановки окон.
		// Запускается после каждого React-commit где изменились internalPanes или percents.
		// Читает визуальные позиции ДО перестановки из flipOldPositions и применяет
		// inverse-transform, чтобы элементы выглядели на старых местах, затем
		// плавно убирает transform — и элементы "едут" на новые места.
		useLayoutEffect(() => {
			const oldPos = flipOldPositions.current;
			if (!oldPos || oldPos.size === 0) return;
			flipOldPositions.current = null;

			if (!containerRef.current) return;
			const paneEls = Array.from(
				containerRef.current.querySelectorAll<HTMLElement>(
					":scope > .split-pane__pane",
				),
			);

			paneEls.forEach((el) => {
				const oldLeft = oldPos.get(el);
				if (oldLeft === undefined) return;

				const newLeft = el.getBoundingClientRect().left;
				const delta = Math.round(oldLeft - newLeft);
				const isFlipDragged = el.classList.contains(
					"split-pane__pane--flip-dragged",
				);

				// Сбрасываем возможные inline-стили от предыдущего FLIP
				el.style.transition = "";
				el.style.transform = "";

				if (Math.abs(delta) < 1) {
					el.style.zIndex = "";
					el.classList.remove("split-pane__pane--flip-dragged");
					return;
				}

				// Только перетаскиваемое окно держим поверх остальных —
				// transform на других элементах уже создаёт stacking context
				if (isFlipDragged) {
					el.style.zIndex = "100";
				}

				// Invert: ставим элемент на его старое место (мгновенно, без transition)
				el.style.transition = "none";
				el.style.transform = `translateX(${delta}px)`;
				void el.offsetWidth; // Force reflow

				// Play: убираем offset с анимацией
				el.style.transition = "transform 250ms ease";
				el.style.transform = "";

				const cleanup = () => {
					el.style.transition = "";
					el.style.zIndex = "";
					el.classList.remove("split-pane__pane--flip-dragged");
				};

				// Основная очистка по transitionend
				el.addEventListener("transitionend", cleanup, { once: true });
				// Fallback: если transitionend не сработал (анимация прервана), очищаем вручную
				setTimeout(cleanup, 300);
			});
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [internalPanes, percents]);

		// Навешиваем dragToReorder только на ширине ≥ breakpoint (см. useSplitPaneLayoutInteractive)
		React.useEffect(() => {
			if (!isLayoutInteractive) return () => {};
			const cleanup = dragToReorder(
				containerRef.current,
				(from, to, oldPositions) =>
					handleReorderRef.current(from, to, oldPositions),
			);
			return () => {
				if (typeof cleanup === "function") cleanup();
			};
		}, [internalPanes.length, isLayoutInteractive]);

		const getPosPercent = (clientX: number, clientY: number) => {
			const rect = containerRef.current!.getBoundingClientRect();
			if (isHorizontal) {
				const x = clientX - rect.left;
				return (x / rect.width) * 100;
			} else {
				const y = clientY - rect.top;
				return (y / rect.height) * 100;
			}
		};

		const onMouseMove = (e: MouseEvent) => {
			if (draggingIndex.current === null || !containerRef.current) return;
			const p = getPosPercent(e.clientX, e.clientY);

			if (internalPanes.length === 2) {
				const newP = clamp(p, minPercent, 100 - minPercent);
				setPercents([newP]);
			} else if (internalPanes.length === 3) {
				const p1 = percents[0];
				const p2 = percents[1];
				if (draggingIndex.current === 0) {
					const S = p1 + p2;
					const minP1 = minPercent;
					const maxP1 = Math.max(minPercent, S - minPercent);
					const newP1 = clamp(p, minP1, maxP1);
					const newP2 = clamp(S - newP1, minPercent, 100 - minPercent - newP1);
					setPercents([newP1, newP2]);
				} else if (draggingIndex.current === 1) {
					const newMiddle = clamp(p - p1, minPercent, 100 - p1 - minPercent);
					setPercents([p1, newMiddle]);
				}
			}
		};

		const onMouseUp = () => {
			draggingIndex.current = null;
			containerRef.current?.classList.remove("split-pane--resizing");
			window.removeEventListener("mousemove", onMouseMove);
			window.removeEventListener("mouseup", onMouseUp);

			// После отпускания курсора — пересчитаем текущие веса и обновим modulesConfig в localStorage
			try {
				let percentsFromDOM: number[] | undefined;
				if (containerRef.current) {
					const children = Array.from(
						containerRef.current.children,
					) as HTMLElement[];
					const paneEls = children.filter((el) =>
						el.classList ? el.classList.contains("split-pane__pane") : false,
					);
					if (paneEls && paneEls.length > 0) {
						const sizes: number[] = [];
						let total = 0;
						paneEls.forEach((el) => {
							const rect = el.getBoundingClientRect();
							const size = isHorizontal ? rect.width : rect.height;
							sizes.push(size);
							total += size;
						});
						if (total > 0) {
							percentsFromDOM = sizes.map((s) =>
								Number(((s / total) * 100).toFixed(3)),
							);
						}
					}
				}

				if (isHorizontal) {
					calculateCurrrentWeight(internalPanes, percentsFromDOM ?? percents);
				} else {
					calculateCurrentSections(internalPanes, percentsFromDOM ?? percents);
				}
			} catch (e) {
				console.warn("Error while calculating current weight", e);
			}
		};

		const startDrag = (index: number, e: React.MouseEvent) => {
			if (!isLayoutInteractive) return;
			e.preventDefault();
			draggingIndex.current = index;
			containerRef.current?.classList.add("split-pane--resizing");
			window.addEventListener("mousemove", onMouseMove);
			window.addEventListener("mouseup", onMouseUp);
		};

		useImperativeHandle(ref, () => ({
			reset: () => {
				if (internalPanes.length === 3) setPercents([33.333, 33.333]);
				else if (internalPanes.length === 2) setPercents([50]);
			},
			setPercents: (p: number[]) => {
				if (internalPanes.length === 2 && p[0] != null)
					setPercents([clamp(p[0], minPercent, 100 - minPercent)]);
				if (internalPanes.length === 3 && p[0] != null && p[1] != null) {
					const a = clamp(p[0], minPercent, 100 - minPercent - p[1]);
					const b = clamp(p[1], minPercent, 100 - minPercent - p[0]);
					setPercents([a, b]);
				}
			},
		}));

		// Если проп initialPercents изменился или состав панелей поменялся — сбрасываем начальные значения
		React.useEffect(() => {
			if (
				props.initialPercents &&
				props.initialPercents.length === panes.length
			) {
				setPercents(
					props.initialPercents.map((v) =>
						clamp(v, minPercent, 100 - minPercent),
					),
				);
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps -- paneIdentitySig и panes.length покрывают смену состава без лишних проходов по panes[]
		}, [paneIdentitySig, panes.length, props.initialPercents]);

		// Вычисляем flex-basis для каждой панели (на ≤1200px — только из CSS, см. breakpoint с isLayoutInteractive)
		const paneStyles: React.CSSProperties[] = (() => {
			const count = internalPanes.length;
			if (!isLayoutInteractive) {
				return Array.from({ length: count }, () => ({}));
			}
			if (count === 1) return [{ flexBasis: "100%" }];
			if (count === 2)
				return [
					{ flexBasis: `${percents[0]}%` },
					{ flexBasis: `${100 - percents[0]}%` },
				];
			const p1 = percents[0];
			const p2 = percents[1];
			return [
				{ flexBasis: `${p1}%` },
				{ flexBasis: `${p2}%` },
				{ flexBasis: `${100 - p1 - p2}%` },
			];
		})();

		return (
			<div
				ref={containerRef}
				className={`split-pane ${isHorizontal ? "split-pane--horizontal" : "split-pane--vertical"}${!isLayoutInteractive ? " split-pane--interaction-disabled" : ""}`}>
				{internalPanes.map((pane, i) => (
					<React.Fragment key={(pane as React.ReactElement)?.key ?? i}>
						<div
							className={`split-pane__pane split-pane__pane--${i}`}
							style={paneStyles[i]}>
							{pane}
						</div>
						{i < internalPanes.length - 1 && (
							<div
								className={`split-pane__resizer ${isHorizontal ? "split-pane__resizer--horizontal" : ""}`}
								onMouseDown={(e) => startDrag(i, e)}
								role='separator'
								aria-orientation={isHorizontal ? "vertical" : "horizontal"}
							/>
						)}
					</React.Fragment>
				))}
			</div>
		);
	},
);

SplitPane.displayName = "SplitPane";
